import { useAtomValue, useSetAtom } from 'jotai'
import { useCallback } from 'react'

import { maxUint256, type Hex } from 'viem'
import { useConfig, useWalletClient } from 'wagmi'

import { useCowAnalytics } from '@cowprotocol/analytics'
import { useFeatureFlags } from '@cowprotocol/common-hooks'
import { createCowLogger, getExplorerTwapOrderLink, normalizeError } from '@cowprotocol/common-utils'
import { type AccountAddress, isEvmChain, OrderKind } from '@cowprotocol/cow-sdk'
import { CurrencyAmount, Token } from '@cowprotocol/currency'
import { PermitHookData } from '@cowprotocol/permit-utils'
import { ComposableCowPoller } from '@cowprotocol/sdk-composable'
import { COW_SHED_2_1_0_VERSION, CowShedSdk } from '@cowprotocol/sdk-cow-shed'
import { UiOrderType } from '@cowprotocol/types'
import {
  isEoaAtom,
  isSafeAppAtom,
  isSafeViaWcAtom,
  isSafeWalletAtom,
  useIsTxBundlingSupported,
  useSendBatchTransactions,
  useWalletDetails,
  useWalletInfo,
} from '@cowprotocol/wallet'
import { WidgetHookEvents } from '@cowprotocol/widget-lib'

import { useSetOptimisticAllowance } from 'entities/optimisticAllowance/useSetOptimisticAllowance'
import { OrderTabId } from 'entities/routes/routes.atom'
import { Nullish } from 'types'

import { assertFactoryDeployed, ADVANCED_ORDERS_ACCOUNT_PROXY_CONFIG, hasBytecode } from 'modules/accountProxy'
import { useAdvancedOrdersDerivedState, useUpdateAdvancedOrdersRawState } from 'modules/advancedOrders'
import { uploadAppDataDocOrderbookApi, useAppData } from 'modules/appData'
import { useGetAmountToSignApprove } from 'modules/erc20Approve'
import { buildTradeWidgetHookPayload, callWidgetHook } from 'modules/injectedWidget'
import { emitPostedOrderEvent } from 'modules/orders'
import { placedOrderHighlightAtom, useRevealOrderInOrdersTable } from 'modules/ordersTable'
import { useGeneratePermitHook, usePermitInfo } from 'modules/permit'
import { getCowSoundSend } from 'modules/sounds'
import { useTradeConfirmActions, useTradePriceImpact } from 'modules/trade'
import { TradeFlowAnalyticsContext, useTradeFlowAnalytics } from 'modules/trade/utils/tradeFlowAnalytics'

import { CowSwapAnalyticsCategory } from 'common/analytics/types'
import { useConfirmPriceImpactWithoutFee } from 'common/hooks/useConfirmPriceImpactWithoutFee'
import { TradeType } from 'common/modules/tradeNavigation'
import { getAreBridgeCurrencies } from 'common/utils/getAreBridgeCurrencies'

import { useEoaTwapFlowUpdater } from './useEoaTwapSigningStep'
import { useExtensibleFallbackContext } from './useExtensibleFallbackContext'
import { useTwapOrder } from './useTwapOrder'
import { useTwapOrderCreationContext } from './useTwapOrderCreationContext'

import { COMPOSABLE_COW_POLLER_ADDRESS } from '../composable-cow-poller/composable-cow-poller.constants'
import { injectPollFundsPreHookIntoAppData } from '../composable-cow-poller/injectPollFundsPreHookIntoAppData'
import { DEFAULT_TWAP_EXECUTION, TWAP_HANDLER_ADDRESS } from '../const'
import {
  canUseEoaTwapPermit,
  ensureEoaTwapSpenderAllowance,
  getEoaTwapApprovalNeeds,
} from '../services/twap/eoa/ensureEoaTwapSpenderAllowance'
import { placeEoaTwapOrder } from '../services/twap/eoa/placeEoaTwapOrder'
import { placeSafeTwapOrder } from '../services/twap/safe/placeSafeTwapOrder'
import { EoaTwapSigningPhase, EoaTwapSigningSteps } from '../state/eoaTwapSigningStepAtom'
import { addTwapOrderToListAtom } from '../state/twapOrdersListAtom'
import { TwapOrderItem, TwapOrderStatus } from '../types'
import { buildEoaTwapSigningStepPlan } from '../utils/buildEoaTwapSigningStepPlan'
import {
  assertTwapOrderSalt,
  buildTwapOrderParamsStruct,
  createTwapOrderSalt,
} from '../utils/buildTwapOrderParamsStruct'
import { EoaTwapPlacementCancelledError, startEoaTwapPlacement } from '../utils/eoaTwapPlacementCancel'
import { getConditionalOrderId } from '../utils/getConditionalOrderId'
import { getEoaTwapPollerAmountToApprove } from '../utils/getEoaTwapPollerAmountToApprove'
import { getErrorMessage } from '../utils/parseTwapError'
import { twapOrderToStruct } from '../utils/twapOrderToStruct'

interface TwapAnalyticsEvent {
  category: CowSwapAnalyticsCategory.TWAP
  action: string
  label: string
  isEoaTwap: boolean
}

interface TwapConversionEvent extends TwapAnalyticsEvent {
  action: 'Conversion'
  label: `${string}|${'no-handler' | 'handler-set'}`
}

interface TwapOrderEvent extends TwapAnalyticsEvent {
  action: 'Place Order'
  label: `${UiOrderType.TWAP}|${string}`
}

const log = createCowLogger('CreateTwapOrder')

// TODO: Break down this large function into smaller functions
// TODO: Add proper return type annotation
// eslint-disable-next-line max-lines-per-function, @typescript-eslint/explicit-function-return-type
export function useCreateTwapOrder() {
  const { chainId, account } = useWalletInfo()
  const { allowsOffchainSigning } = useWalletDetails()
  const twapOrder = useTwapOrder()
  const addTwapOrderToList = useSetAtom(addTwapOrderToListAtom)
  const setPlacedOrderHighlight = useSetAtom(placedOrderHighlightAtom)
  const revealOrderInOrdersTable = useRevealOrderInOrdersTable()
  const setOptimisticAllowance = useSetOptimisticAllowance()
  const isSafeWallet = useAtomValue(isSafeWalletAtom)
  const isSafeApp = useAtomValue(isSafeAppAtom)
  const isSafeViaWc = useAtomValue(isSafeViaWcAtom)
  const isTxBundlingSupported = useIsTxBundlingSupported()
  const isEoa = useAtomValue(isEoaAtom)
  const { isTwapEoaEnabled } = useFeatureFlags()
  const config = useConfig()

  const { inputCurrencyAmount, outputCurrencyAmount } = useAdvancedOrdersDerivedState()

  const appDataInfo = useAppData()
  const sendSafeTransactions = useSendBatchTransactions()

  const amountToSignApprove = useGetAmountToSignApprove()
  // The line above can be misleading because `useGetAmountToSignApprove` checks against the vault relayer allowance,
  // but we want to approve the poller instead, and it can return null when the form is not ready yet or `0` when the
  // vault relayer already covers the trade.
  // Note permit does not use this value. It always permits the exact TWAP sell (`amountToCover`).
  const amountToApprove = amountToSignApprove ? BigInt(amountToSignApprove.quotient.toString()) : maxUint256
  const twapOrderCreationContext = useTwapOrderCreationContext(
    inputCurrencyAmount as Nullish<CurrencyAmount<Token>>,
    amountToApprove,
  )
  const extensibleFallbackContext = useExtensibleFallbackContext()

  const { data: walletClient } = useWalletClient()

  // Poller permit only: ADVANCED_ORDERS disables permit for the trade spender, so look up poller as SWAP + custom spender.
  const pollerAddress = chainId && isEvmChain(chainId) ? COMPOSABLE_COW_POLLER_ADDRESS[chainId] : undefined
  const pollerPermitInfo = usePermitInfo(inputCurrencyAmount?.currency, TradeType.SWAP, pollerAddress)
  const generatePermitHook = useGeneratePermitHook()

  const updateAdvancedOrdersState = useUpdateAdvancedOrdersRawState()

  const tradeConfirmActions = useTradeConfirmActions()
  const updateEoaTwapFlow = useEoaTwapFlowUpdater()

  const { priceImpact } = useTradePriceImpact()
  const isBridge = getAreBridgeCurrencies(inputCurrencyAmount?.currency, outputCurrencyAmount?.currency)
  const { confirmPriceImpactWithoutFee } = useConfirmPriceImpactWithoutFee(isBridge)

  const analytics = useCowAnalytics()
  const tradeFlowAnalytics = useTradeFlowAnalytics()

  const sendOrderAnalytics = useCallback(
    (context: string, isEoaTwap: boolean) => {
      const analyticsEvent: TwapOrderEvent = {
        category: CowSwapAnalyticsCategory.TWAP,
        action: 'Place Order',
        label: `${UiOrderType.TWAP}|${context}`,
        isEoaTwap,
      }
      analytics.sendEvent(analyticsEvent)
    },
    [analytics],
  )

  const sendTwapConversionAnalytics = useCallback(
    (status: string, fallbackHandlerIsNotSet: boolean, isEoaTwap: boolean) => {
      const analyticsEvent: TwapConversionEvent = {
        category: CowSwapAnalyticsCategory.TWAP,
        action: 'Conversion',
        label: `${status}|${fallbackHandlerIsNotSet ? 'no-handler' : 'handler-set'}`,
        isEoaTwap,
      }
      analytics.sendEvent(analyticsEvent)
    },
    [analytics],
  )

  return useCallback(
    // TODO: Break down this large function into smaller functions
    // TODO: Reduce function complexity by extracting logic
    // eslint-disable-next-line max-lines-per-function, complexity
    async (fallbackHandlerIsNotSet: boolean): Promise<boolean | undefined> => {
      const isSafeTwap = (isSafeApp === true || isSafeViaWc === true) && isSafeWallet && isTxBundlingSupported === true
      const isEoaTwap = isSafeApp === false && !!isTwapEoaEnabled && isEoa === true

      if (!isEvmChain(chainId) || (!isSafeTwap && !isEoaTwap)) {
        return
      }

      if (!chainId || !account) return
      if (!inputCurrencyAmount || !outputCurrencyAmount || !appDataInfo || !twapOrder) return

      if (isEoaTwap) {
        if (!walletClient || !twapOrderCreationContext) return
      } else if (
        !twapOrderCreationContext ||
        chainId !== twapOrderCreationContext.chainId ||
        !extensibleFallbackContext
      ) {
        return
      }

      const isPriceImpactConfirmed = await confirmPriceImpactWithoutFee(priceImpact)

      if (!isPriceImpactConfirmed) {
        return
      }

      const pendingTrade = {
        inputAmount: inputCurrencyAmount,
        outputAmount: outputCurrencyAmount,
      }

      const orderType = UiOrderType.TWAP

      const twapFlowAnalyticsContext: TradeFlowAnalyticsContext = {
        account,
        recipient: twapOrder.receiver,
        recipientAddress: twapOrder.receiver,
        marketLabel: [inputCurrencyAmount.currency.symbol, outputCurrencyAmount.currency.symbol].join(','),
        orderType,
        isEoaTwap,
      }

      const placementSignal = startEoaTwapPlacement()

      try {
        const isWidgetHookPassed = await callWidgetHook(
          WidgetHookEvents.ON_BEFORE_TRADE,
          buildTradeWidgetHookPayload({
            orderType,
            inputAmount: inputCurrencyAmount,
            outputAmount: outputCurrencyAmount,
            recipient: twapOrder.receiver,
            orderKind: OrderKind.SELL,
            chainId,
          }),
        )

        if (!isWidgetHookPassed) {
          return
        }

        const eoaPoller = isEoaTwap ? requireEoaTwapPollerAddress(chainId, pollerAddress) : undefined

        let salt: Hex | undefined
        let updatedAppData = appDataInfo
        let updatedTwapOrder = twapOrder
        let isProxyDeployed = false

        if (eoaPoller) {
          salt = assertTwapOrderSalt(createTwapOrderSalt())

          const cowShedSdk = new CowShedSdk(
            undefined,
            ADVANCED_ORDERS_ACCOUNT_PROXY_CONFIG.factoryOptions,
            COW_SHED_2_1_0_VERSION,
          )
          await assertFactoryDeployed(
            config,
            ADVANCED_ORDERS_ACCOUNT_PROXY_CONFIG.factoryOptions.factoryAddress,
            `chain ${chainId}`,
          )
          const proxyAddress = cowShedSdk.getCowShedAccount(chainId, account) as `0x${string}`
          isProxyDeployed = await hasBytecode(config, proxyAddress)

          // Schedule id is appData-independent; compute before injecting pollFunds into appData.
          const scheduleId = new ComposableCowPoller(eoaPoller).getScheduleId({
            funder: account,
            handler: TWAP_HANDLER_ADDRESS[chainId],
            owner: proxyAddress,
            salt,
          }) as Hex

          updatedAppData = await injectPollFundsPreHookIntoAppData(appDataInfo, {
            pollerAddress: eoaPoller,
            scheduleId,
          })
          updatedTwapOrder = { ...twapOrder, appData: updatedAppData.appDataKeccak256 }
        }

        const paramsStruct = buildTwapOrderParamsStruct(chainId, updatedTwapOrder, salt)

        // ComposableCoW order hash (keccak256 of params). Matches indexed `order.hash` until eventId arrives; not a CoW orderbook UID.
        const twapOrderHash = getConditionalOrderId(paramsStruct)

        tradeConfirmActions.onSign(pendingTrade)

        // No quote/quoteId exists yet at this point for either TWAP path:
        tradeFlowAnalytics.placeAdvancedOrder({
          ...twapFlowAnalyticsContext,
          allowsOffchainSigning,
        })

        sendTwapConversionAnalytics('posted', fallbackHandlerIsNotSet, isEoaTwap)

        await uploadAppDataDocOrderbookApi({
          appDataKeccak256: updatedAppData.appDataKeccak256,
          fullAppData: updatedAppData.fullAppData,
          chainId,
          env: 'prod', // Since WatchTower creates orders only in PROD env, we should have `prod` here
        })

        if (placementSignal.aborted) {
          return
        }

        // Safe only. `= safeTxHash`. Empty for EOA.
        let orderCreationHash = ''

        // Value passed to `tradeConfirmActions.onSuccess`. Ends up in `PostedOrderNotification`, rendering a Safe or Explorer link in a toast.
        // Must be truthy to show the success screen.
        // - EOA: indexed event ID, or setup transaction hash if indexing times out
        // - Safe: safeTxHash
        let confirmModalHash: string

        let safeAddressOrCowShedAddress: string
        let orderStatus: TwapOrderStatus
        let eventId: string | undefined

        if (eoaPoller) {
          if (!walletClient) return

          const sellTokenAddress = updatedTwapOrder.sellAmount.currency.address as `0x${string}`
          const sellToken = updatedTwapOrder.sellAmount.currency
          const sellAmountAtoms = BigInt(updatedTwapOrder.sellAmount.quotient.toString())
          const pollerAmountToApprove = getEoaTwapPollerAmountToApprove({
            amountToApprove,
            sellAmount: sellAmountAtoms,
            isDaiLikePermit: pollerPermitInfo?.type === 'dai-like',
          })

          const pollerApprovalNeeds = await getEoaTwapApprovalNeeds({
            config,
            account: account as `0x${string}`,
            sellTokenAddress,
            spender: eoaPoller,
            amountToCover: sellAmountAtoms,
            amountToApprove: pollerAmountToApprove,
          })

          const pollerCanUsePermit = canUseEoaTwapPermit(pollerPermitInfo, pollerAmountToApprove)
          const pollerNeeds = { ...pollerApprovalNeeds, canUsePermit: pollerCanUsePermit }

          const signingStepPlan = buildEoaTwapSigningStepPlan({
            poller: pollerNeeds,
          })

          // Open the multi-step pending UI as soon as the plan is known.
          const firstStep = signingStepPlan[0]

          if (firstStep) {
            updateEoaTwapFlow({ step: firstStep, phase: EoaTwapSigningPhase.Sign, plan: signingStepPlan })
          }

          let pollerPermitData: PermitHookData | null = null

          if (pollerApprovalNeeds.needsApproval) {
            // EIP-2612 permits the exact TWAP sell. Dai-like finite amounts use on-chain approve.
            pollerPermitData = await ensureEoaTwapSpenderAllowance({
              config,
              chainId,
              account: account as `0x${string}`,
              sellTokenAddress,
              sellTokenName: sellToken.name,
              sellTokenAmount: sellAmountAtoms,
              amountToPermitOrApprove: pollerAmountToApprove,
              spender: eoaPoller,
              permitInfo: pollerPermitInfo,
              generatePermitHook,
              step: EoaTwapSigningSteps.ApprovePoller,
              permitStep: EoaTwapSigningSteps.PermitPoller,
              zeroStep: EoaTwapSigningSteps.ZeroApprovePoller,
              onSigningStep: updateEoaTwapFlow,
              approvalNeeds: pollerNeeds,
            })
          }

          const {
            proxyAddress,
            setupTxHash,
            setupBlockNumber,
            eventId: eventIdParam,
          } = await placeEoaTwapOrder({
            isProxyDeployed,
            chainId,
            account: account as `0x${string}`,
            twapOrder: updatedTwapOrder,
            twapOrderCreationContext,
            twapOrderHash,
            paramsStruct,
            config,
            walletClient,
            onSigningStep: updateEoaTwapFlow,
            pollerPermitData,
          })

          if (pollerApprovalNeeds.needsApproval) {
            setOptimisticAllowance({
              chainId,
              tokenAddress: sellTokenAddress,
              owner: account,
              spender: eoaPoller,
              amount: pollerAmountToApprove,
              blockNumber: setupBlockNumber,
            })
          }

          // Setup factory tx hash for confirm-modal / explorer link.
          confirmModalHash = setupTxHash
          safeAddressOrCowShedAddress = proxyAddress
          orderStatus = TwapOrderStatus.Pending
          orderCreationHash = setupTxHash
          eventId = eventIdParam
        } else {
          const { safeTxHash, safeAddress } = await placeSafeTwapOrder({
            twapOrder,
            twapOrderCreationContext,
            paramsStruct,
            fallbackHandlerIsNotSet,
            extensibleFallbackContext,
            sendSafeTransactions,
            amountToApprove,
          })
          orderCreationHash = safeTxHash
          confirmModalHash = safeTxHash
          safeAddressOrCowShedAddress = safeAddress // === account
          orderStatus = TwapOrderStatus.WaitSigning
        }

        const orderItem: TwapOrderItem = {
          order: twapOrderToStruct(updatedTwapOrder),
          status: orderStatus,
          chainId,
          safeAddress: safeAddressOrCowShedAddress,
          resolvedOwner: isEoaTwap ? account : safeAddressOrCowShedAddress,
          submissionDate: new Date().toISOString(),
          id: twapOrderHash,
          executionInfo: { ...DEFAULT_TWAP_EXECUTION },
        }

        addTwapOrderToList(orderItem)

        getCowSoundSend().play()

        emitPostedOrderEvent({
          chainId,
          id: twapOrderHash,
          orderCreationHash,
          explorerUrl: isEoaTwap ? (eventId ? getExplorerTwapOrderLink(chainId, eventId) : null) : undefined,
          kind: OrderKind.SELL,
          receiver: twapOrder.receiver,
          inputAmount: updatedTwapOrder.sellAmount,
          outputAmount: updatedTwapOrder.buyAmount,
          owner: account,
          uiOrderType: orderType,
          isEoaTwap,
        })

        sendOrderAnalytics(`${orderType}|${twapFlowAnalyticsContext.marketLabel}`, isEoaTwap)

        tradeFlowAnalytics.sign(twapFlowAnalyticsContext)
        sendTwapConversionAnalytics('signed', fallbackHandlerIsNotSet, isEoaTwap)

        // The order is real, but a newer placement now owns the form and the confirm modal.
        if (placementSignal.aborted) return true

        updateAdvancedOrdersState({ recipient: null, recipientAddress: null })

        const ordersTableTab = isEoaTwap ? OrderTabId.OPEN : OrderTabId.SIGNING
        const orderIdToReveal = isEoaTwap ? (eventId ?? twapOrderHash) : twapOrderHash
        setPlacedOrderHighlight({ orderId: orderIdToReveal, tabId: ordersTableTab })

        if (isEoaTwap) {
          // Keep the review card open and replace signing steps with the inline success box.
          updateEoaTwapFlow({
            step: EoaTwapSigningSteps.Success,
            phase: EoaTwapSigningPhase.Confirmed,
            eventId,
            lockDismiss: false,
          })
        } else {
          updateEoaTwapFlow(null)
          tradeConfirmActions.onSuccess(confirmModalHash)
        }

        revealOrderInOrdersTable(orderIdToReveal, ordersTableTab)

        // Keep the confirm modal frozen (quote countdown hidden, amounts locked) while the EOA
        // success card stays open. TradeConfirmation treats a falsy return as an aborted confirm.
        return true
      } catch (err: unknown) {
        // A Safe request can settle long after a newer placement took over the confirm modal.
        if (err instanceof EoaTwapPlacementCancelledError || placementSignal.aborted) {
          return
        }

        const error = normalizeError(err)

        log.error(error)
        const errorMessage = getErrorMessage(error)
        updateEoaTwapFlow(null)
        tradeConfirmActions.onError(errorMessage)
        tradeFlowAnalytics.error(error, errorMessage, twapFlowAnalyticsContext)
        sendTwapConversionAnalytics('rejected', fallbackHandlerIsNotSet, isEoaTwap)

        return false
      }
    },
    [
      isTwapEoaEnabled,
      isSafeWallet,
      isEoa,
      isSafeApp,
      isSafeViaWc,
      isTxBundlingSupported,
      allowsOffchainSigning,
      config,
      chainId,
      account,
      inputCurrencyAmount,
      outputCurrencyAmount,
      twapOrderCreationContext,
      extensibleFallbackContext,
      sendSafeTransactions,
      appDataInfo,
      twapOrder,
      confirmPriceImpactWithoutFee,
      priceImpact,
      tradeConfirmActions,
      addTwapOrderToList,
      setPlacedOrderHighlight,
      updateAdvancedOrdersState,
      sendOrderAnalytics,
      sendTwapConversionAnalytics,
      tradeFlowAnalytics,
      revealOrderInOrdersTable,
      pollerAddress,
      pollerPermitInfo,
      generatePermitHook,
      walletClient,
      updateEoaTwapFlow,
      amountToApprove,
      setOptimisticAllowance,
    ],
  )
}

function requireEoaTwapPollerAddress(chainId: number, poller: AccountAddress | undefined): AccountAddress {
  if (!poller) {
    throw new Error(`ComposableCowPoller is not deployed on chain ${chainId}`)
  }

  return poller
}
